class CollectionSortOverlay extends HTMLElement {
  static AB_TEST_CLASS_A = 'ab-test-collection-sort-overlay-variant-a';
  static AB_TEST_CLASS_B = 'ab-test-collection-sort-overlay-variant-b';
  static LIVE_CLASS = 'collection-sort-overlay-live';
  static AB_TEST_WAIT_MS = 2000;
  static STORAGE_KEY = 'collection-sort-overlay-sort';

  static SORT_LABELS = {
    'best-selling': 'Most Popular',
    'created-descending': "What's New",
    'price-descending': 'Price High to Low',
    'price-ascending': 'Price Low to High',
  };

  static ALLOWED_SORTS = Object.keys(CollectionSortOverlay.SORT_LABELS);

  static normalizeSort(sortValue) {
    if (sortValue && CollectionSortOverlay.ALLOWED_SORTS.includes(sortValue)) {
      return sortValue;
    }

    return null;
  }

  static getStoredSort() {
    return CollectionSortOverlay.normalizeSort(sessionStorage.getItem(CollectionSortOverlay.STORAGE_KEY));
  }

  static saveSort(sortValue) {
    const normalized = CollectionSortOverlay.normalizeSort(sortValue);

    if (normalized) {
      sessionStorage.setItem(CollectionSortOverlay.STORAGE_KEY, normalized);
    } else {
      sessionStorage.removeItem(CollectionSortOverlay.STORAGE_KEY);
    }
  }

  static getActiveSortForTabs() {
    const fromUrl = CollectionSortOverlay.normalizeSort(new URLSearchParams(window.location.search).get('sort_by'));
    if (fromUrl) return fromUrl;

    return CollectionSortOverlay.getStoredSort();
  }

  static updateCollectionTabLinks() {
    if (sessionStorage.getItem('collection-quick-filter')) return;

    const scroller = document.querySelector('[data-collection-tabs]');
    if (!scroller) return;

    const activeSort = CollectionSortOverlay.getActiveSortForTabs();

    scroller.querySelectorAll('.collection-tabs__item').forEach((link) => {
      const baseUrl = link.dataset.collectionUrl || link.href.split('?')[0];

      if (activeSort) {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('sort_by', activeSort);
        link.href = url.toString();
      } else {
        link.href = baseUrl;
      }
    });
  }

  static initCollectionTabs() {
    const scroller = document.querySelector('[data-collection-tabs]');
    if (!scroller) return;

    scroller.querySelectorAll('.collection-tabs__item').forEach((link) => {
      if (!link.dataset.collectionUrl) {
        link.dataset.collectionUrl = link.href.split('?')[0];
      }
    });
  }

  constructor() {
    super();
    this.triggers = [];
    this.activeTrigger = null;
    this.dialog = null;
    this.applyButton = null;
    this.valueLabels = [];
    this.inputs = [];
    this.closeTargets = [];
    this.pendingSort = null;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onApplyClick = this.onApplyClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onCloseTargetClick = this.onCloseTargetClick.bind(this);
    this.onCollectionReloaded = this.onCollectionReloaded.bind(this);

    this.refreshElements();
    this.bindEvents();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bootstrap(), { once: true });
    } else {
      this.bootstrap();
    }

    document.addEventListener('collection:reloaded', this.onCollectionReloaded);
    window.addEventListener('popstate', this.onCollectionReloaded);
  }

  static cleanupOrphanedDialogs(activeDialog = null) {
    document.querySelectorAll('.collection-sort-overlay__dialog').forEach((dialog) => {
      if (dialog !== activeDialog) dialog.remove();
    });
  }

  getDialogElement() {
    const dialogId = this.dataset.sectionId ? `CollectionSortOverlay-${this.dataset.sectionId}` : null;

    return (
      this.querySelector('.collection-sort-overlay__dialog') ||
      (dialogId ? document.getElementById(dialogId) : null)
    );
  }

  refreshElements() {
    const sectionId = this.dataset.sectionId;

    this.triggers = sectionId
      ? [...document.querySelectorAll(`.collection-sort-overlay__trigger[data-section-id="${sectionId}"]`)]
      : [...this.querySelectorAll('.collection-sort-overlay__trigger')];

    this.valueLabels = this.triggers.flatMap((trigger) => [...trigger.querySelectorAll('[data-sort-label]')]);
    this.dialog = this.getDialogElement();

    if (!this.dialog) return;

    CollectionSortOverlay.cleanupOrphanedDialogs(this.dialog);

    this.applyButton = this.dialog.querySelector('.collection-sort-overlay__apply');
    this.inputs = this.dialog.querySelectorAll('input[name="collection-sort-overlay"]');
    this.closeTargets = this.dialog.querySelectorAll('[data-sort-overlay-close]');
  }

  bindEvents() {
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onTriggerClick);
      trigger.addEventListener('click', this.onTriggerClick);
    });

    this.applyButton?.removeEventListener('click', this.onApplyClick);
    this.applyButton?.addEventListener('click', this.onApplyClick);

    this.inputs.forEach((input) => {
      input.removeEventListener('change', this.onInputChange);
      input.addEventListener('change', this.onInputChange);
    });

    this.closeTargets.forEach((target) => {
      target.removeEventListener('click', this.onCloseTargetClick);
      target.addEventListener('click', this.onCloseTargetClick);
    });

    document.removeEventListener('keydown', this.onKeydown);
    document.addEventListener('keydown', this.onKeydown);
  }

  async bootstrap() {
    CollectionSortOverlay.initCollectionTabs();
    this.syncSortFromUrl();
    this.maybeRestoreStoredSort();
    CollectionSortOverlay.updateCollectionTabLinks();

    const visible = await this.resolveVisibility();

    if (!visible) return;

    this.refreshElements();
    this.bindEvents();
    this.portalDialog();
    this.setButtonVariant(this.getButtonVariant());
    this.syncButtonLabel();
  }

  syncSortFromUrl() {
    const fromUrl = CollectionSortOverlay.normalizeSort(new URLSearchParams(window.location.search).get('sort_by'));
    if (fromUrl) {
      CollectionSortOverlay.saveSort(fromUrl);
    }
  }

  maybeRestoreStoredSort() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('sort_by')) return;
    if (sessionStorage.getItem('collection-quick-filter')) return;

    const stored = CollectionSortOverlay.getStoredSort();
    if (!stored || typeof FacetFiltersForm === 'undefined') return;

    params.set('sort_by', stored);
    FacetFiltersForm.renderPage(params.toString(), null, true);
  }

  requiresAbTestClass() {
    return this.dataset.intelligemsOnly === 'true';
  }

  isLiveEnabled() {
    return (
      document.body.classList.contains(CollectionSortOverlay.LIVE_CLASS) ||
      document.body.classList.contains(CollectionSortOverlay.AB_TEST_CLASS_A) ||
      document.body.classList.contains(CollectionSortOverlay.AB_TEST_CLASS_B)
    );
  }

  isVisible() {
    if (document.body.classList.contains('shopify-design-mode')) return true;
    if (this.requiresAbTestClass() === false) return true;
    return (
      document.body.classList.contains(CollectionSortOverlay.AB_TEST_CLASS_A) ||
      document.body.classList.contains(CollectionSortOverlay.AB_TEST_CLASS_B) ||
      document.body.classList.contains(CollectionSortOverlay.LIVE_CLASS)
    );
  }

  resolveVisibility() {
    if (!this.requiresAbTestClass() || this.isLiveEnabled()) {
      return Promise.resolve(this.isVisible());
    }

    if (this.isVisible()) return Promise.resolve(true);

    return new Promise((resolve) => {
      const finish = (visible) => {
        observer.disconnect();
        clearInterval(interval);
        resolve(visible);
      };

      const observer = new MutationObserver(() => {
        if (this.isVisible()) finish(true);
      });

      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      const deadline = Date.now() + CollectionSortOverlay.AB_TEST_WAIT_MS;
      const interval = setInterval(() => {
        if (this.isVisible()) {
          finish(true);
        } else if (Date.now() >= deadline) {
          finish(false);
        }
      }, 50);
    });
  }

  getButtonVariant() {
    if (document.body.classList.contains(CollectionSortOverlay.AB_TEST_CLASS_B)) {
      return 'variant_b';
    }

    if (document.body.classList.contains(CollectionSortOverlay.AB_TEST_CLASS_A)) {
      return 'variant_a';
    }

    if (this.dataset.buttonStyle === 'variant_b') {
      return 'variant_b';
    }

    if (this.triggers.some((trigger) => trigger.classList.contains('collection-sort-overlay__trigger--variant_b'))) {
      return 'variant_b';
    }

    return 'variant_a';
  }

  setButtonVariant(variant) {
    this.triggers.forEach((trigger) => {
      trigger.classList.remove('collection-sort-overlay__trigger--variant_a', 'collection-sort-overlay__trigger--variant_b');
      trigger.classList.add(`collection-sort-overlay__trigger--${variant}`);
    });
  }

  normalizeSort(sortValue) {
    return CollectionSortOverlay.normalizeSort(sortValue);
  }

  getAppliedSort() {
    const params = new URLSearchParams(window.location.search);
    return this.normalizeSort(params.get('sort_by'));
  }

  getOverlayDefaultSort() {
    return this.normalizeSort(this.dataset.overlayDefaultSort) || 'best-selling';
  }

  getPopupPreselectSort() {
    return this.getAppliedSort() || this.getOverlayDefaultSort();
  }

  getSortLabel(sortValue) {
    return CollectionSortOverlay.SORT_LABELS[sortValue] || CollectionSortOverlay.SORT_LABELS['best-selling'];
  }

  syncButtonLabel() {
    const applied = this.getAppliedSort();

    this.valueLabels.forEach((valueLabel) => {
      if (applied) {
        valueLabel.textContent = this.getSortLabel(applied);
        valueLabel.hidden = false;
      } else {
        valueLabel.textContent = '';
        valueLabel.hidden = true;
      }
    });
  }

  syncOptionSelection() {
    this.inputs.forEach((input) => {
      const option = input.closest('.collection-sort-overlay__option');
      option?.classList.toggle('is-selected', input.checked);
    });
  }

  preselectCurrentSort() {
    const preselectSort = this.getPopupPreselectSort();
    this.pendingSort = preselectSort;

    this.inputs.forEach((input) => {
      input.checked = input.value === preselectSort;
    });

    this.syncOptionSelection();
    this.updateApplyState();
  }

  updateApplyState() {
    if (!this.applyButton) return;
    this.applyButton.disabled = !this.pendingSort;
  }

  onInputChange(event) {
    this.pendingSort = event.target.value;

    this.inputs.forEach((input) => {
      input.checked = input === event.target;
    });

    this.syncOptionSelection();
    this.updateApplyState();
  }

  onTriggerClick(event) {
    if (!this.isVisible()) return;

    this.activeTrigger = event.currentTarget;
    this.refreshElements();
    this.bindEvents();
    this.preselectCurrentSort();
    this.open();
  }

  portalDialog() {
    if (!this.dialog) return;

    CollectionSortOverlay.cleanupOrphanedDialogs(this.dialog);

    if (this.dialog.parentElement !== document.body) {
      document.body.appendChild(this.dialog);
    }
  }

  onCloseTargetClick() {
    this.close();
  }

  onDialogClick(event) {
    const panel = this.dialog?.querySelector('.collection-sort-overlay__panel');
    if (panel?.contains(event.target)) return;
    this.close();
  }

  open() {
    if (!this.dialog) this.dialog = this.getDialogElement();
    if (!this.dialog || this.triggers.length === 0) return;

    this.portalDialog();
    this.dialog.hidden = false;
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('collection-sort-overlay-open');
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.querySelector('.collection-sort-overlay__close')?.focus();
  }

  close() {
    if (!this.dialog) this.dialog = this.getDialogElement();
    if (!this.dialog || this.triggers.length === 0) return;

    this.dialog.hidden = true;
    this.dialog.removeEventListener('click', this.onDialogClick);
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('collection-sort-overlay-open');
    (this.activeTrigger || this.triggers[0])?.focus();
    this.activeTrigger = null;
  }

  onApplyClick() {
    if (!this.pendingSort || typeof FacetFiltersForm === 'undefined') return;

    sessionStorage.removeItem('collection-quick-filter');
    CollectionSortOverlay.saveSort(this.pendingSort);

    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', this.pendingSort);

    FacetFiltersForm.renderPage(params.toString(), null, true);
    CollectionSortOverlay.updateCollectionTabLinks();
    this.syncButtonLabel();
    this.close();
  }

  onKeydown(event) {
    if (this.dialog?.hidden) return;
    if (event.key === 'Escape') this.close();
  }

  onCollectionReloaded() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      this.close();
      this.syncSortFromUrl();
      CollectionSortOverlay.updateCollectionTabLinks();

      if (!this.isVisible()) return;

      this.refreshElements();
      this.bindEvents();
      this.portalDialog();
      this.setButtonVariant(this.getButtonVariant());
      this.syncButtonLabel();
    });
  }
}

customElements.define('collection-sort-overlay', CollectionSortOverlay);
